import { useCallback, useEffect, useState } from "react";

import useService from "../../../../hooks/useService";
import { getModalHandler } from "../../Common/Modals/hooks/useModalSingleton";
import { useSettlementStore } from "../store/settlementStore";

import useSettlementPermissions from "./useSettlementPermissions";

function normalizeCurrencyValue(value) {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value);
}

export default function useEditableCurrencyCell({
  detail,
  fieldKey,
  value,
  onSaveSuccess,
}) {
  const providerSettlementRepository = useService("repositories/provider-settlement-repository");
  const { openFailureModal } = getModalHandler();
  const bumpRefreshVersion = useSettlementStore((state) => state.bumpRefreshVersion);
  const { canEditMedicalBreakdown } = useSettlementPermissions();
  const normalizedValue = normalizeCurrencyValue(value);
  const [draftValue, setDraftValue] = useState(normalizedValue);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setDraftValue(normalizedValue);
    }
  }, [isEditing, normalizedValue]);

  const handleEdit = useCallback(() => {
    if (!canEditMedicalBreakdown) {
      return;
    }

    setIsEditing(true);
  }, [canEditMedicalBreakdown]);

  const handleCancel = useCallback(() => {
    setDraftValue(normalizedValue);
    setIsEditing(false);
  }, [normalizedValue]);

  const handleChange = useCallback((event) => {
    setDraftValue(event.target.value);
  }, []);

  const handleSave = useCallback(async () => {
    if (!canEditMedicalBreakdown) {
      return;
    }

    setIsSaving(true);

    try {
      await providerSettlementRepository.patch(detail.pk, {
        [fieldKey]: draftValue === "" ? null : Number(draftValue),
      });
      bumpRefreshVersion();
      setIsEditing(false);
      onSaveSuccess?.();
    } catch (error) {
      openFailureModal(error);
    } finally {
      setIsSaving(false);
    }
  }, [
    bumpRefreshVersion,
    canEditMedicalBreakdown,
    detail?.pk,
    draftValue,
    fieldKey,
    onSaveSuccess,
    openFailureModal,
    providerSettlementRepository,
  ]);

  return {
    draftValue,
    isEditing,
    isSaving,
    canEditMedicalBreakdown,
    handleCancel,
    handleChange,
    handleEdit,
    handleSave,
  };
}
